// src/components/public/LandingPage.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Truck as TruckIcon, FileCheck, ArrowRight, } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import ExchangeRateWidget from './ExchangeRateWidget';

const LandingPage = () => {
  const { t } = useLanguage();

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative bg-primary-900 text-white">
        <div className="absolute inset-0 z-0 opacity-20 bg-primary-950">
          {/* We're using a solid background color instead of an image */}
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight">
              {t('heroTitle')}
            </h1>
            <p className="mt-6 text-xl text-primary-100 max-w-2xl mx-auto">
              {t('heroSubtitle')}
            </p>
            <div className="mt-10 sm:flex sm:justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <Link 
                to="/consultation" 
                className="px-8 py-4 text-lg font-medium rounded-md bg-secondary-600 hover:bg-secondary-700 flex items-center justify-center"
              >
                {t('learnMore')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link 
                to="/insuranceGE" 
                className="px-8 py-4 text-lg font-medium rounded-md border border-white bg-transparent hover:bg-white hover:text-primary-900 flex items-center justify-center"
              >
                {t('getInsurance')}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Services Section */}
      <div className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-neutral-900 sm:text-4xl">
              {t('ourServices')}
            </h2>
            <p className="mt-4 text-lg text-neutral-600 max-w-2xl mx-auto">
              {t('servicesSubtitle')}
            </p>
          </div>

          <div className="mt-12 grid gap-8 md:grid-cols-3">
            {/* Registration Service */}
            <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center text-center">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 text-primary-600 mb-4">
                <TruckIcon className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">{t('registrationService')}</h3>
              <p className="text-neutral-600 flex-grow">
                {t('registrationServiceDesc')}
              </p>
              <Link to="/consultation" className="mt-4 text-primary-600 hover:text-primary-800 flex items-center">
                {t('readMore')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Insurance Service */}
            <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center text-center">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 text-primary-600 mb-4">
                <ShieldCheck className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">{t('insuranceService')}</h3>
              <p className="text-neutral-600 flex-grow">
                {t('insuranceServiceDesc')}
              </p>
              <Link to="/insuranceGE" className="mt-4 text-primary-600 hover:text-primary-800 flex items-center">
                {t('readMore')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>

            {/* Documentation Service */}
            <div className="bg-white rounded-lg shadow-md p-6 flex flex-col items-center text-center">
              <div className="flex items-center justify-center h-16 w-16 rounded-full bg-primary-100 text-primary-600 mb-4">
                <FileCheck className="h-8 w-8" />
              </div>
              <h3 className="text-xl font-bold text-neutral-900 mb-2">{t('documentationService')}</h3>
              <p className="text-neutral-600 flex-grow">
                {t('documentationServiceDesc')}
              </p>
              <Link to="/consultation" className="mt-4 text-primary-600 hover:text-primary-800 flex items-center">
                {t('readMore')}
                <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Exchange Rate Widget */}
      <div className="py-8 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <ExchangeRateWidget />
          </div>
        </div>
      </div>

      {/* Insurance Highlight Section */}
      <div className="py-16 bg-primary-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div>
              <h2 className="text-3xl font-extrabold sm:text-4xl">
                {t('insuranceTitle')}
              </h2>
              <p className="mt-4 text-lg text-primary-200">
                {t('insuranceHighlightDesc')}
              </p>
              <ul className="mt-8 space-y-4">
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <ShieldCheck className="h-6 w-6 text-secondary-400" />
                  </div>
                  <p className="ml-3 text-lg text-primary-200">
                    {t('insuranceBenefit1')}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <ShieldCheck className="h-6 w-6 text-secondary-400" />
                  </div>
                  <p className="ml-3 text-lg text-primary-200">
                    {t('insuranceBenefit2')}
                  </p>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0">
                    <ShieldCheck className="h-6 w-6 text-secondary-400" />
                  </div>
                  <p className="ml-3 text-lg text-primary-200">
                    {t('insuranceBenefit3')}
                  </p>
                </li>
              </ul>
              <div className="mt-10">
                <Link 
                  to="/insuranceGE" 
                  className="px-6 py-3 text-base font-medium rounded-md bg-secondary-600 hover:bg-secondary-700 flex items-center w-max"
                >
                  {t('getInsuranceNow')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
            <div className="mt-12 lg:mt-0">
              <div className="relative">
                <div className="aspect-w-5 aspect-h-3 rounded-lg shadow-xl overflow-hidden bg-primary-700 p-8">
                  <div className="w-full h-64 bg-primary-600 rounded-lg flex items-center justify-center">
                    <div className="text-white text-center px-4">
                      <ShieldCheck className="h-12 w-12 mx-auto mb-4"/>
                      <h3 className="text-xl font-bold">{t('insuranceService')}</h3>
                      <p className="mt-2 text-primary-100">{t('insuranceServiceDesc')}</p>
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-primary-900 opacity-10 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary-600">2000+</div>
              <div className="mt-2 text-sm font-medium text-neutral-600">{t('registrationsCompleted')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary-600">500+</div>
              <div className="mt-2 text-sm font-medium text-neutral-600">{t('insurancePoliciesIssued')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary-600">95%</div>
              <div className="mt-2 text-sm font-medium text-neutral-600">{t('satisfactionRate')}</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-extrabold text-primary-600">24/7</div>
              <div className="mt-2 text-sm font-medium text-neutral-600">{t('supportAvailable')}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Company Credentials Section */}
      <div className="py-12 bg-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-8 items-center">
            <div>
              <h2 className="text-3xl font-extrabold text-neutral-900">
                {t('companyName')}
              </h2>
              <div className="mt-4">
                <img
                  src="/TTSLogo.png" // Update this path to your image
                  alt="Transport Transit Service"
                  className="w-full h-auto rounded-lg"
                />
              </div>
              <ul className="mt-8 space-y-4">
              </ul>
            </div>
            <div className="mt-10 lg:mt-0 lg:pl-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-xl font-bold text-neutral-900 mb-4">{t('OurClients')}</h3>
                <div className="space-y-6">
                  <div className="border-l-4 border-primary-500 pl-4 italic text-neutral-600">
                    {t('Client1Com')}
                    <p className="mt-2 font-medium text-neutral-800">{t('Client1')}</p>
                  </div>
                  <div className="border-l-4 border-primary-500 pl-4 italic text-neutral-600">
                    {t('Client2Com')}
                    <p className="mt-2 font-medium text-neutral-800">{t('Client2')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact CTA */}
      <div className="bg-neutral-50 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-neutral-900">{t('contactCTA')}</h2>
            <p className="mt-4 text-lg text-neutral-600">{t('contactCTADesc')}</p>
            <div className="mt-8">
              <Link 
                to="/consultation" 
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
              >
                {t('contactUs')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;